/**
 * Static guardrail category messages (chat v2, AIVA-5929 / AIVA-6559 / AIVA-6560).
 *
 * When the backend terminates a turn with `guardrail_flagged`, the partial
 * response is replaced with a static message — never a model-generated
 * apology. The category → message mapping is owned here in the frontend so
 * product can tune the copy without a backend deploy. Categories are the
 * Azure AI Content Safety names plus the backend's "unavailable" sentinel
 * (fail-closed outage) and its "context_blocked" quarantine sentinel.
 *
 * The event's `sources` say WHICH screened input flagged (AIVA-6560): the
 * user's own message, or the pre-loaded context the add-in sent with the turn
 * — the open email body, the open Word document text, an attachment. The copy
 * has to follow the source. AIVA-6579 widened the screen to all of that
 * content, which made the guard correct and left the notice wrong: telling
 * users their "Summarize" click was flagged for hateful content, when the
 * EMAIL was the problem, misreports what happened and reads as an accusation.
 */

/**
 * Flagged categories, as phrases that compose into a sentence.
 *
 * The first four are Azure AI Content Safety's harm categories. The last two
 * come from the confidentiality screen (AIVA-6579,
 * `shared/services/guardrails/sensitive_content.py`) — a credential-phishing
 * mail scores zero on all four harm categories, so it needed its own question
 * and its own copy. Both sides must be changed together; a category with no
 * entry here falls back to the generic message.
 */
const CATEGORY_PHRASES: Record<string, string> = {
  hate: "hateful or discriminatory content",
  selfharm: "self-harm related content",
  sexual: "sexual content",
  violence: "violent content",
  credential_solicitation: "a request for passwords, security codes or privileged access",
  secret_disclosure: "exposed passwords, keys or security codes",
};

/**
 * What each context source is called in the notice.
 *
 * Keys are the backend's source names — `_GUARDRAIL_SOURCE_BY_OWNER` in
 * `API/routes/v2/chat.py`. A name with no entry here falls back to message
 * copy, so the two sides must be changed together.
 */
const SOURCE_LABELS: Record<string, string> = {
  email: "the open email",
  document: "the open document",
  attachment: "the attached file",
};

const UNAVAILABLE_MESSAGE =
  "This conversation was stopped because the safety screening service is currently unavailable. Please try again later.";

// AIVA-6559 — the screen withheld the OPEN email / document / attachment, not
// the user's own message. Previously this case had no notice at all: the backend
// left a placeholder in the prompt and the model described the block in its own
// words, so the sentence the user read changed from turn to turn.
//
// Kept character-identical to _QUARANTINE_BLOCK_MESSAGE in
// shared/services/guardrails/guarded_model.py, which is the same block reached
// via a quarantined TOOL result. Both entry points must read the same, so change
// the two together. Names no skill's verb — every skill lands here, not just
// summarise (PR review, AIVA-6559).
//
// A whole message, not a category phrase: `context_blocked` is the quarantine
// outcome rather than a content-safety category, so it never composes with a
// source label the way a flagged category does (AIVA-6560).
const QUARANTINE_BLOCK_MESSAGE = "This message is quarantined for manual review. So I can't work with it here.";

const GENERIC_GUARDRAIL_MESSAGE =
  "This conversation was stopped because the message was flagged by the content safety screen.";

/**
 * The first flagged source that maps to a context label, or `null` when the
 * user's own message is implicated.
 *
 * Empty sources mean the backend named none — an older build, or a verdict from
 * an outage where nothing was graded — and read as the message, which is both
 * the pre-attribution behaviour and the conservative one. `message` present
 * alongside a context source also wins: what the user typed is the stronger
 * fact, and the notice must never blame the email for it.
 */
function contextSourceLabel(sources: readonly string[]): string | null {
  if (sources.length === 0 || sources.includes("message")) return null;
  for (const source of sources) {
    const label = SOURCE_LABELS[source];
    if (label) return label;
  }
  return null;
}

/** The static notice for a flagged turn. Uses the first mapped category. */
export function guardrailMessage(categories: string[], sources: readonly string[] = []): string {
  const lowered = categories.map((c) => c.toLowerCase());
  if (lowered.includes("unavailable")) return UNAVAILABLE_MESSAGE;
  if (lowered.includes("context_blocked")) return QUARANTINE_BLOCK_MESSAGE;
  const phrase = lowered.map((c) => CATEGORY_PHRASES[c]).find((p): p is string => Boolean(p));
  const contextLabel = contextSourceLabel(sources);
  if (contextLabel) {
    // The user did nothing wrong — the content they asked AiVA to act on was
    // flagged. Say so, and say clearly that AiVA won't act on it (the reply /
    // summary the quick action promised is not coming).
    const reason = phrase ? `was flagged for ${phrase}` : "was flagged by the content safety screen";
    return `This conversation was stopped because the content of ${contextLabel} ${reason}. AiVA can't summarize, reply to, or otherwise act on it.`;
  }
  if (phrase) return `This conversation was stopped because the message was flagged for ${phrase}.`;
  return GENERIC_GUARDRAIL_MESSAGE;
}
